import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from 'easy-peasy';
import Modal from './Modal';
import { httpGetMines, httpGetMineShafts } from 'services/mines';
import './EditPumpModal.scss';

export default function AddPoolModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { createPool } = useStoreActions((actions) => actions.pools);
  const [formData, setFormData] = useState({
    name: '',
    mine: '',
    mine_shaft: '',
  });
  const [mines, setMines] = useState([]);
  const [mineShafts, setMineShafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingShafts, setLoadingShafts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const fetchMines = async () => {
      try {
        setLoading(true);
        const res = await httpGetMines();
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setMines(data);
        setError(null);
      } catch (err) {
        console.error('Error loading mines:', err);
        setError(t('pool_modal_mines_error'));
        setMines([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMines();

    setFormData({
      name: '',
      mine: '',
      mine_shaft: '',
    });
    setMineShafts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when modal opens; avoid refetch on language change
  }, [isOpen]);

  const handleMineChange = async (mineId) => {
    setFormData((prev) => ({ ...prev, mine: mineId, mine_shaft: '' }));
    setMineShafts([]);

    if (mineId) {
      setLoadingShafts(true);
      try {
        const response = await httpGetMineShafts(mineId);
        const shaftsData = Array.isArray(response.data) ? response.data : response.data.results || [];
        setMineShafts(shaftsData);
      } catch (err) {
        console.error('Error fetching mine shafts:', err);
        setError(t('pool_modal_shafts_error'));
        setMineShafts([]);
      } finally {
        setLoadingShafts(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const submitData = {
        name: formData.name,
        shaft: parseInt(formData.mine_shaft, 10),
      };

      const newPool = await createPool(submitData);

      if (onSuccess) {
        onSuccess(newPool);
      }
      onClose();
    } catch (err) {
      console.error('Error creating pool:', err);
      setError(t('pool_modal_create_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('pool_modal_add_title')} size="medium">
      {loading ? (
        <div className="text-center py-5">{t('ui_loading')}</div>
      ) : (
        <form className="edit-modal-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('pool_modal_name_required')}</label>
            <input
              className="form-input"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('pool_modal_placeholder_name')}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('pool_modal_mine')} *</label>
            <select
              className="form-select"
              value={formData.mine}
              onChange={(e) => handleMineChange(e.target.value)}
              required
            >
              <option value="">{t('pool_modal_select_mine')}</option>
              {mines.map((mine) => (
                <option key={mine.id} value={mine.id}>
                  {mine.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">{t('pool_modal_shaft')} *</label>
            <select
              className="form-select"
              value={formData.mine_shaft}
              onChange={(e) => handleInputChange('mine_shaft', e.target.value)}
              disabled={!formData.mine || loadingShafts}
              required
            >
              <option value="">{t('pool_modal_select_shaft')}</option>
              {loadingShafts && <option>{t('ui_loading')}</option>}
              {!loadingShafts &&
                mineShafts.map((shaft) => (
                  <option key={shaft.id} value={shaft.id}>
                    {shaft.name}
                  </option>
                ))}
            </select>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || !formData.name || !formData.mine_shaft}
            >
              {saving ? t('pool_modal_creating_pool') : t('pool_modal_create_btn')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
